/**
 * ============================================================================
 * ReviewsNew - PRODUCTION-READY REVIEWS & RATINGS PAGE
 * ============================================================================
 *
 * Production-ready reviews management with:
 * - TanStack Query for data fetching & caching
 * - Compact card design for 100+ reviews
 * - Fullscreen image modal with keyboard navigation
 * - Mobile-responsive layout
 * - Real-time reply management
 * - Export functionality
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - 5min cache for better UX (reviews don't change frequently)
 * - Optimistic UI updates for replies
 * - Lazy loading images
 * - Virtualized list for 100+ reviews (future enhancement)
 *
 * ============================================================================
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useVenueRole } from '../hooks/useVenueRole';
import { useConfirmation } from '../contexts/ConfirmationContext';
import { AlertCircle, RefreshCw, Download, Search, MessageSquare } from 'lucide-react';
import { useReviews, useCreateReply, useUpdateReply, useDeleteReply } from '../hooks/useReviews';
import { ReviewStats } from '../components/reviews/ReviewStats';
import { ReviewCard } from '../components/reviews/ReviewCard';
import { RatingDistribution } from '../components/reviews/RatingDistribution';
import type { ReviewFilters } from '../types/reviews';

/**
 * Reviews page - production-ready rebuild
 */
export default function ReviewsNew() {
  const { user } = useAuth();
  const { alert } = useConfirmation();
  const { can } = useVenueRole();
  const venue = user?.venues?.[0];
  // The Reviews tab is visible to insights.view (read) OR reviews.reply; only
  // reviews.reply may actually reply/edit/delete replies.
  const canReply = can('reviews.reply');

  // ============================================================================
  // STATE - Filters and pagination
  // ============================================================================

  const [filters, setFilters] = useState<ReviewFilters>({
    search: '',
    rating: 'all',
    status: 'all',
  });
  const [currentPage, setCurrentPage] = useState(1);

  // ============================================================================
  // DATA FETCHING (TanStack Query)
  // ============================================================================

  const { data, isPending, error, refetch } = useReviews({
    venueId: venue?.id,
    page: currentPage,
    filters,
  });

  const createReplyMutation = useCreateReply();
  const updateReplyMutation = useUpdateReply();
  const deleteReplyMutation = useDeleteReply();

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleFilterChange = (newFilters: Partial<ReviewFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleReply = async (reviewId: number, replyText: string) => {
    if (!venue?.id || !canReply) return;
    await createReplyMutation.mutateAsync({
      venueId: venue.id,
      reviewId,
      data: { reply: replyText },
    });
  };

  const handleUpdateReply = async (reviewId: number, replyText: string) => {
    if (!venue?.id || !canReply) return;
    await updateReplyMutation.mutateAsync({
      venueId: venue.id,
      reviewId,
      data: { reply: replyText },
    });
  };

  const handleDeleteReply = async (reviewId: number) => {
    if (!venue?.id || !canReply) return;
    await deleteReplyMutation.mutateAsync({
      venueId: venue.id,
      reviewId,
    });
  };

  const handleExport = async () => {
    if (!data?.reviews || data.reviews.length === 0) {
      await alert({
        title: 'No Data',
        message: 'No reviews available to export.',
        variant: 'info',
      });
      return;
    }

    const headers = ['Customer', 'Rating', 'Comment', 'Date', 'Has Reply', 'Verified'];
    const rows = data.reviews.map((review) => [
      review.customer_name,
      `${review.rating}/5`,
      review.comment.replace(/"/g, '""'), // Escape quotes
      review.formatted_date,
      review.has_reply ? 'Yes' : 'No',
      review.is_verified ? 'Yes' : 'No',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `reviews_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading reviews...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // ERROR STATE
  // ============================================================================

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Reviews</h2>
        <p className="text-gray-600 mb-4">
          {error instanceof Error ? error.message : 'An error occurred'}
        </p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // ============================================================================
  // NO VENUE STATE
  // ============================================================================

  if (!venue) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Venue Found</h2>
        <p className="text-gray-600">
          Please ensure you have a venue associated with your account.
        </p>
      </div>
    );
  }

  const isSubmitting =
    createReplyMutation.isPending ||
    updateReplyMutation.isPending ||
    deleteReplyMutation.isPending;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1">
            Reviews & Ratings
          </h1>
          <p className="text-gray-600 text-sm">
            Manage customer feedback and respond to reviews
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={!data?.reviews || data.reviews.length === 0}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Stats Cards */}
      {data?.stats && <ReviewStats stats={data.stats} />}

      {/* Rating Distribution */}
      {data?.stats && <RatingDistribution stats={data.stats} />}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search in comments..."
              value={filters.search}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-blue-500 text-sm"
            />
          </div>

          {/* Rating Filter */}
          <select
            value={filters.rating}
            onChange={(e) =>
              handleFilterChange({ rating: e.target.value as ReviewFilters['rating'] })
            }
            className="px-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-blue-500 bg-white text-sm font-medium"
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) =>
              handleFilterChange({ status: e.target.value as ReviewFilters['status'] })
            }
            className="px-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-blue-500 bg-white text-sm font-medium"
          >
            <option value="all">All Reviews</option>
            <option value="replied">Replied</option>
            <option value="unanswered">Unanswered</option>
          </select>
        </div>
      </div>

      {/* Reviews List - Compact Grid */}
      <div className="space-y-3">
        {data?.reviews && data.reviews.length > 0 ? (
          data.reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onReply={handleReply}
              onUpdateReply={handleUpdateReply}
              onDeleteReply={handleDeleteReply}
              isSubmitting={isSubmitting}
              canReply={canReply}
            />
          ))
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No reviews found</h3>
            <p className="text-gray-600">
              {filters.search || filters.rating !== 'all' || filters.status !== 'all'
                ? 'Try adjusting your filters'
                : 'No reviews yet. Reviews will appear here once customers leave feedback.'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.last_page > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between bg-white rounded-xl border border-gray-200 p-4 gap-3">
          <p className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
            Showing page {data.pagination.current_page} of {data.pagination.last_page}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, data.pagination.last_page) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage((p) => Math.min(data.pagination.last_page, p + 1))}
              disabled={currentPage === data.pagination.last_page}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
